import { useMemo } from "react";
import { useWatch } from "react-hook-form";
import { useIntl } from "react-intl";

import { ReactComponent as PackageIcon } from "assets/images/iconsV2/package.svg";
import { ReactComponent as VolumeCubicMeterIcon } from "assets/images/iconsV2/volume-cubic-meter.svg";
import { ReactComponent as WeightIcon } from "assets/images/iconsV2/weight.svg";
import { PackingListDropdown } from "@RequestFormV2/sections/PackingList/components/shared/PackingListSelect";
import testIds from "constants/testIds";
import usePermissions from "hooks/usePermissions";
import { useUnitSystemPreferences } from "@RequestFormV2/hooks/useUnitSystemPreferences";
import type { PackingListRow } from "@RequestFormV2/sections/PackingList/types";
import {
  calculatePackingListTotals,
  formatPackingListCbm,
  formatPackingListWeight,
} from "@RequestFormV2/sections/PackingList/utils/packingListCalculations";
// import PackingListImport from "./PackingListImport";
import "./PackingListHeader.scss";

export default function PackingListHeader() {
  const intl = useIntl();
  const { parsedPermissions } = usePermissions();
  const { weightUnit } = useUnitSystemPreferences();
  const packingList = (useWatch({ name: "packing_list" }) as PackingListRow[] | undefined) ?? [];

  const companySettings = parsedPermissions.company_shipper_settings ?? {};

  const totals = useMemo(() => calculatePackingListTotals(packingList, weightUnit), [packingList, weightUnit]);

  const goodsEstimationOptions = useMemo(
    () => [
      {
        label: intl.formatMessage({
          id: "request-form.packing-list.goodsAreEstimated",
          defaultMessage: "Goods are estimated",
        }),
        value: "Estimated",
      },
      {
        label: intl.formatMessage({
          id: "request-form.packing-list.goodsAreFinalized",
          defaultMessage: "Goods are finalized",
        }),
        value: "Finalized",
      },
    ],
    [intl],
  );

  return (
    <div className="rfv2-packing-list-header">
      <div className="rfv2-packing-list-header__totals">
        <div className="rfv2-packing-list-header__stat rfv2-packing-list-header__stat--weight">
          <div className="rfv2-packing-list-header__stat-label-group">
            <WeightIcon />
            <span>{intl.formatMessage({ id: "general.totalWeight", defaultMessage: "Total weight" })}</span>
          </div>
          <strong data-testid={testIds.REQUEST_FORM_PACKING_LIST_TOTAL_WEIGHT}>
            {formatPackingListWeight(totals.totalWeight)}
            <span className="rfv2-packing-list-header__unit"> {weightUnit}</span>
          </strong>
        </div>
        <div className="rfv2-packing-list-header__stat rfv2-packing-list-header__stat--cbm">
          <div className="rfv2-packing-list-header__stat-label-group">
            <VolumeCubicMeterIcon />
            <span>{intl.formatMessage({ id: "request-form.packing-list.totalCbm", defaultMessage: "Total CBM" })}</span>
          </div>
          <strong data-testid={testIds.REQUEST_FORM_PACKING_LIST_TOTAL_CBM}>
            {formatPackingListCbm(totals.totalCBM)}
            <span className="rfv2-packing-list-header__unit"> m³</span>
          </strong>
        </div>
        <div className="rfv2-packing-list-header__stat rfv2-packing-list-header__stat--units">
          <div className="rfv2-packing-list-header__stat-label-group">
            <PackageIcon />
            <span>{intl.formatMessage({ id: "request-form.packing-list.totalUnits", defaultMessage: "Total units" })}</span>
          </div>
          <strong>{totals.totalUnits}</strong>
        </div>
      </div>

      <div className="rfv2-packing-list-header__actions">
        {companySettings.goods_list_estimation && (
          <div className="rfv2-packing-list-header__goods-estimation">
            <PackingListDropdown
              name="organizational.goods_confirmation_type"
              options={goodsEstimationOptions}
              triggerTestId={testIds.REQUEST_FORM_PACKING_LIST_GOODS_ESTIMATION}
            />
          </div>
        )}
        {/* <PackingListImport /> */}
      </div>
    </div>
  );
}
